import {
  classRankGap,
  differentialGap,
  formatEvidence,
  formatRank,
} from "@/components/pipeline/pipelineDesign";

export interface FitNumbers {
  playerRank?: number | null;
  programRank?: number | null;
  playerDifferential?: number | null;
  programDifferential?: number | null;
}

interface FitComparisonProps extends FitNumbers {
  /** "lines" for cards, "compact" for list rows, "tiles" for the school detail panel. */
  variant?: "lines" | "compact" | "tiles";
  className?: string;
}

function differential(value?: number | null) {
  return value == null || !Number.isFinite(value) ? "—" : formatEvidence(value);
}

/** "61 spots behind their recent commits" -> "61 spots behind"; "0.3 strokes better than the program average" -> "0.3 strokes better" */
function shortGap(gap: string): string {
  return gap
    .replace(/ (than )?the program average$/, "")
    .replace(/ (of )?their recent commits$/, "")
    .replace(/^Right at$/, "Even");
}

/** Tightest form for list rows: "61 behind", "0.3 better", "Even". */
function tinyGap(gap: string): string {
  return shortGap(gap).replace(/ (spots?|strokes?) /, " ");
}

function isAhead(player?: number | null, program?: number | null, epsilon = 0) {
  return player != null && program != null && player - program < -epsilon;
}

/**
 * The player's class rank and scoring differential next to the program's recent
 * commits. Class rank comes first because the Dream / Realistic / Safety tier is
 * based on it; the differential adds context. Lower is better for both.
 */
export function FitComparison({
  playerRank,
  programRank,
  playerDifferential,
  programDifferential,
  variant = "lines",
  className = "",
}: FitComparisonProps) {
  const hasRank = playerRank != null || programRank != null;
  const hasDifferential = playerDifferential != null || programDifferential != null;
  if (!hasRank && !hasDifferential) return null;

  const rows = [
    hasRank && {
      label: "Class rank",
      you: formatRank(playerRank),
      them: formatRank(programRank),
      themLabel: "Recent commits avg",
      gap: classRankGap(playerRank, programRank),
      ahead: isAhead(playerRank, programRank, 2),
    },
    hasDifferential && {
      label: "Differential",
      you: differential(playerDifferential),
      them: differential(programDifferential),
      themLabel: "Program average",
      gap: differentialGap(playerDifferential, programDifferential),
      ahead: isAhead(playerDifferential, programDifferential, 0.04),
    },
  ].filter(Boolean) as Array<{
    label: string;
    you: string;
    them: string;
    themLabel: string;
    gap: string | null;
    ahead: boolean;
  }>;

  if (variant === "tiles") {
    return (
      <div className={`space-y-3 ${className}`}>
        {rows.map(row => (
          <div key={row.label}>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-xl bg-surface p-3">
                <span className="block text-[11px] font-semibold text-subtle">
                  Your {row.label.toLowerCase()}
                </span>
                <strong className="mt-0.5 block text-lg font-bold tabular-nums text-forest">
                  {row.you}
                </strong>
              </div>
              <div className="rounded-xl bg-surface p-3">
                <span className="block text-[11px] font-semibold text-subtle">{row.themLabel}</span>
                <strong className="mt-0.5 block text-lg font-bold tabular-nums text-forest">
                  {row.them}
                </strong>
              </div>
            </div>
            {row.gap && (
              <p
                className={`mt-1.5 text-[11px] font-semibold ${row.ahead ? "text-primary-text" : "text-muted-foreground"}`}
              >
                {row.gap} <span className="font-normal text-subtle">· lower is better</span>
              </p>
            )}
          </div>
        ))}
        {hasRank && (
          <p className="text-[11px] text-subtle">
            The Dream, Realistic or Safety tier is based on class rank.
          </p>
        )}
      </div>
    );
  }

  if (variant === "compact") {
    return (
      <dl className={`space-y-0.5 text-xs ${className}`}>
        {rows.map(row => (
          <div key={row.label} className="flex items-baseline gap-1.5 whitespace-nowrap">
            <dt className="w-8 shrink-0 text-subtle">
              {row.label === "Class rank" ? "Rank" : "Diff"}
            </dt>
            <dd className="font-semibold tabular-nums text-forest-soft">
              {row.you} vs {row.them}
            </dd>
            {row.gap && (
              <dd
                className={`font-semibold ${row.ahead ? "text-primary-text" : "text-muted-foreground"}`}
              >
                {tinyGap(row.gap)}
              </dd>
            )}
          </div>
        ))}
      </dl>
    );
  }

  return (
    <dl className={`space-y-1 text-xs ${className}`}>
      {rows.map(row => (
        <div key={row.label} className="flex flex-wrap items-baseline gap-x-1.5">
          <dt className="text-subtle">{row.label}</dt>
          <dd className="font-semibold tabular-nums text-forest-soft">
            {row.you} vs {row.them}
          </dd>
          {row.gap && (
            <dd
              className={`font-semibold ${row.ahead ? "text-primary-text" : "text-muted-foreground"}`}
            >
              · {shortGap(row.gap)}
            </dd>
          )}
        </div>
      ))}
    </dl>
  );
}
